import cx from 'classnames';
import Link from 'next/link';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';

import useLinkProps from '../../../hooks/use-link-props';
import BPImage from '../../ui/v2/image';
import BackgroundLink from '../../ui/links/background';
import Button from '../../ui/button';
import Chevrelon from '../../ui/icons/cheverlon';
import { useRouter } from 'next/router';
import useLoggedUser from '../../../hooks/use-logged-user';
import useIsMobile from '../../../hooks/use-is-mobile';
import { forwardRef } from 'react';
import {
  Carousel,
  CarouselBullets,
  CarouselContent,
  CarouselItem,
} from '../../ui/carousel-v2';
import { useAppContext } from '../../providers/app-context';

type TMoreParts = {
  car: TProductDetail['car'];
  vehicle: TProductDetail['unique_vehicle'];
  warranty: number;
  uniqueVehicleProducts: Array<{
    id: number;
    sku: string;
    slug: string;
    main_image: string;
    category_slug: string;
    category: string;
    part: string;
    reference: string | null;
    pricing: TPricing;
    category_en: string;
    part_en: string;
    warranty?: number;
  }>;
  ref?: any;
  key?: string;
};

export const MorePartsCard = ({ car, vehicle }: TMoreParts) => {
  const getLinks = useLinkProps();

  return (
    <BackgroundLink
      variant="SECONDARY"
      prefetch={false}
      shallow
      scroll
      passHref
      {...getLinks('vehicle', {
        vehicle: vehicle.slug,
      })}
    >
      <div className="flex flex-1 flex-col lg:flex-row w-[calc(100vw-32px)] lg:w-auto">
        <div className={cx('bg-white')}>
          <div
            className={cx(
              'relative',
              'w-full aspect-video',
              'lg:w-[215px] lg:h-[126px]'
            )}
          >
            <BPImage
              src={
                vehicle.main_image ||
                `${process.env.PUBLIC_PREFIX}/svg/placeholder.svg`
              }
              layout="fill"
              objectFit="cover"
              objectPosition="center"
              alt={`${vehicle.main_image || 'car placeholder'} main image`}
            />
          </div>
          <div className="px-4 py-1">
            <p className="font-bold text-sm">{car.model_name}</p>
            <p className="text-xs">{car.version_name}</p>
          </div>
        </div>
        <div
          className={cx(
            'w-full p-2 lg:w-48',
            'bg-brand-logo text-center',
            'font-bold text-white ',
            'flex flex-col justify-center items-center'
          )}
        >
          <Trans
            i18nKey="product:more_parts_from_same_vehicle"
            components={{
              span: <span className="block text-xl" />,
            }}
            values={{
              quantity: vehicle.stock,
            }}
          />
        </div>
      </div>
    </BackgroundLink>
  );
};

const MoreParts = forwardRef(
  ({ car, vehicle, warranty, uniqueVehicleProducts, key }: TMoreParts, ref) => {
    const { t } = useTranslation();
    const { push, locale = 'en' } = useRouter();
    const getLink = useLinkProps();
    const user = useLoggedUser();
    const isMobile = useIsMobile();
    const { isBparts } = useAppContext();
    const isResaleer = user?.staff.company_type == 'supplier';
    const getLinks = useLinkProps();

    const carImage = (
      <BackgroundLink
        variant="SECONDARY"
        prefetch={false}
        shallow
        scroll
        passHref
        {...getLinks('vehicle', {
          vehicle: vehicle.slug,
        })}
      >
        <div className="max-lg:mx-auto flex flex-1 flex-col lg:flex-row w-full lg:w-auto p-2 boder-white bg-white shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]">
          <div
            className={cx(
              'relative',
              'w-full aspect-video',
              'lg:w-full lg:h-fit'
            )}
          >
            <div className={cx('w-fit h-fit')}>
              <BPImage
                src={
                  vehicle?.main_image ||
                  `${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`
                }
                fill
                objectFit={vehicle?.main_image ? 'cover' : 'contain'}
                objectPosition="center"
                alt={`car main image`}
                key={vehicle.slug}
                priority={false}
              />
            </div>
          </div>
        </div>
      </BackgroundLink>
    );

    return (
      <div
        key={key}
        className={cx(
          'pb-8 lg:pt-16 lg:pb-12 bg-neutral-10 flex flex-col items-center'
        )}
      >
        <section className="container mx-auto ">
          <div className="flex flex-row max-lg:grid max-lg:grid-cols-1 gap-6 lg:gap-12 pb-6">
            <div className="flex flex-col gap-6 lg:gap-12 max-md:px-4 max-lg:pt-8 w-full lg:w-2/3">
              <div className="flex gap-6 flex-col">
                <p
                  className="font-bold text-2xl scroll-mt-[316px] lg:scroll-mt-[210px]"
                  id="pp_see_vehicle"
                  ref={ref}
                >
                  {t(
                    vehicle.stock === 1
                      ? 'product:unique_vehicle.title_one'
                      : 'product:unique_vehicle.title_more',
                    { count: vehicle.stock }
                  )}
                </p>
              </div>
              <div className="lg:hidden">{carImage}</div>
              <div className="flex flex-row gap-4 lg:gap-4 items-start justify-start">
                <div
                  className={cx(
                    'relative',
                    'aspect-video',
                    'shrink-0',
                    'flex h-[48px] w-[48px] lg:h-[60px] lg:w-[60px]',
                    'bg-neutral-10'
                  )}
                >
                  <BPImage
                    src={
                      car.brand_alias
                        ? `${process.env.STATIC_PREFIX}/brands/logos/${car.brand_alias}.svg`
                        : `${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`
                    }
                    layout="fill"
                    objectFit="contain"
                    objectPosition="center"
                    fallbackSrc={`${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`}
                    className={cx('bg-cover mix-blend-multiply')}
                    alt={`${car.brand_alias || 'car placeholder'} logo`}
                    key={`${car.brand_slug || ''}_${car.model_slug || ''}_${car.version_slug || ''
                      }`}
                    priority={false}
                  />
                </div>
                <div className="flex flex-col justify-start gap-2">
                  <span className="font-bold text-base lg:text-2xl">{`${car.brand_name
                    } ${car.model_name || ''} ${car.version_name || ''}`}</span>
                  {car.years && (
                    <span className="text-sm lg:text-[22px]">{car.years}</span>
                  )}
                  {car.door_nr && (
                    <span className="font-bold text-xs lg:text-[18px]">
                      {car.door_nr} {t('vehicle:doors')}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="max-lg:hidden lg:w-1/3 max-[1535px]:pl-4">
              {carImage}
            </div>
          </div>

          <div className="pb-4 lg:pb-8">
            <Carousel
              opts={{
                align: 'center',
              }}
              viewportBasedSlidesToScroll={{
                desktop: 6,
                mobile: 1,
              }}
              //autoplay
              className=""
            >
              <CarouselContent className="pb-2 lg:-ml-6">
                {Object.values(uniqueVehicleProducts).map((item, index) => {
                  return (
                    <CarouselItem
                      key={`img_${index}`}
                      className="pl-0 basis-2/3 lg:basis-1/6 lg:pl-6 lg:grid lg:grid-cols-1 lg:gap-6 max-lg:h-fit"
                    >
                      <BackgroundLink
                        key={index}
                        prefetch={false}
                        shallow
                        scroll
                        {...getLink('product', {
                          category: item.category_slug,
                          subCategory: item.slug,
                        })}
                        className={cx(
                          'relative flex justify-start items-start',
                          'h-full w-full mx-auto',
                          'hover:bg-brand-10 bg-white p-2 boder-white shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]'
                        )}
                      >
                        <div className="relative w-full flex flex-col h-full justify-start items-start gap-2">
                          <div className="relative aspect-video h-auto shrink-0 flex w-full">
                            <BPImage
                              src={
                                item?.main_image ||
                                `${process.env.PUBLIC_PREFIX}/svg/placeholder.svg`
                              }
                              alt={`${item.slug} main image`}
                              fill
                              style={{
                                objectFit: 'cover',
                                objectPosition: 'center',
                              }}
                              key={item.id}
                              priority={false}
                            />
                          </div>
                          <div className="relative w-full h-full flex flex-col justify-between items-start gap-2">
                            <span className="font-bold text-base line-clamp-1">
                              {item.part}
                            </span>
                            <span className="text-sm line-clamp-1">
                              Ref. {item.reference || '-'}
                            </span>
                            {isResaleer ? (
                              <div className="block text-left flex items-end justify-between">
                                <small className="text-red-100 mb-1">
                                  {t('product:resale_price')}
                                </small>
                                <strong className="text-right">
                                  {item.pricing?.price_final_printable ||
                                    t('product:price_on_request')}
                                </strong>
                              </div>
                            ) : (
                              <strong className="block text-left flex items-end justify-start">
                                {item.pricing?.price_final_printable ? (
                                  <div>
                                    <span className="font-extrabold">
                                      {item.pricing?.price_final_printable}
                                    </span>
                                    <div className="text-xs text-green-100">
                                      <Trans
                                        i18nKey={`product:checklist.vat_shipping_${item.pricing.price_message}`}
                                        components={{ b: <b /> }}
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  t('product:price_on_request')
                                )}
                              </strong>
                            )}
                          </div>
                        </div>
                      </BackgroundLink>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <CarouselBullets className="!mt-6" />
            </Carousel>
          </div>

          <section className="w-full flex justify-center max-md:px-4">
            <Button
              variant="GHOST-PRIMARY"
              isFullWidth={false}
              className={cx(
                'flex flex-row gap-6 items-center justify-center lg:justify-between',
                '!text-blue-100'
              )}
              onClick={() => {
                const link = getLink('vehicle', {
                  vehicle: vehicle.slug,
                });
                push(link.href, link.as, { shallow: true });
              }}
            >
              <>{t('common:search-all-parts')}</>
              <Chevrelon
                className={cx(
                  'w-4 h-4 -rotate-90',
                  'text-blue-100'
                )}
              ></Chevrelon>
            </Button>
          </section>
        </section>
      </div>
    );
  }
);

MoreParts.displayName = 'MoreParts';
export default MoreParts;
